import { Component, Inject, Input, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';

import { ApplicationFacade, PurchaseHistoryFacade, TravelCardType } from '@ridango/components';
import { AppStartupService, AuthService, ProductPropertiesType, Ticket, TimeUtil } from '@ridango/core';
import Env from '@ridango/core/env';

import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
    selector: 'rwidget-purchase-history',
    templateUrl: './purchase-history.component.html',
    styleUrls: ['./purchase-history.component.css']
})
export class PurchaseHistoryComponent implements OnInit {
    @Input() showPrintingOption = true;

    tickets$: Observable<Ticket[]> = this.historyFacade.tickets$;
    loading$: Observable<boolean> = this.historyFacade.loading$;
    startDate$: Observable<Date> = this.historyFacade.startDate$;
    endDate$: Observable<Date> = this.historyFacade.endDate$;
    minStartDate$: Observable<Date> = this.historyFacade.minStartDate$;
    minEndDate$: Observable<Date> = this.historyFacade.minEndDate$;
    maxStartDate$: Observable<Date> = this.historyFacade.maxStartDate$;
    maxEndDate$: Observable<Date> = this.historyFacade.maxEndDate$;
    diff$: Observable<number> = this.historyFacade.diff$;

    dateFormat = this.env.PRIMARY_DATE_FORMAT;
    travelCardType = TravelCardType;
    assetsPath$ = this.applicationFacade.assetsPath$;
    purchaseTableColumns: string[] = this.env.PURCHASE_HISTORY_TABLE_COLUMNS;

    constructor(
        private historyFacade: PurchaseHistoryFacade,
        @Inject('ENV') public env: Env,
        public translate: TranslateService,
        private applicationFacade: ApplicationFacade,
        public appStartupService: AppStartupService,
        private http: HttpClient,
        private authService: AuthService
    ) {}

    ngOnInit() {
        this.updateList();
    }

    async download(ticket: Ticket, elem: HTMLElement) {
        elem.setAttribute('disabled', 'disabled');
        await this.historyFacade.getPdfPut(ticket.jwt_token);
        elem.removeAttribute('disabled');
    }

    startUpdated(date: Date) {
        this.historyFacade.setStartDate(date);
    }

    endUpdated(date: Date) {
        this.historyFacade.setEndDate(date);
    }

    handleBackClicked(): void {
        window.history.back();
    }

    updateList() {
        this.historyFacade.fetchPurchaseHistory();
    }

    showProductReceipt(productProperties) {
        if (productProperties) {
            return !productProperties.some((p) => p.code === ProductPropertiesType.DisableReceipt);
        }
        return true;
    }

    async downloadPurchaseHistoryPdf() {
        const token = this.authService.getLocalAccessToken();
        if (!token) {
            console.error('No authentication token available');
            return;
        }

        const regionId = this.env.REGION_ID;

        const startDate = await this.startDate$.pipe(take(1)).toPromise();
        const endDate = await this.endDate$.pipe(take(1)).toPromise();
        const lang = this.translate.currentLang || 'et';

        if (!startDate || !endDate) {
            console.error('Start date and end date are required');
            return;
        }

        const baseUrl = this.env.CORE_API_URL?.replace('/core', '') || '';
        const url = `${baseUrl}/v2/${regionId}/cart/pdf/purchase-history`;

        const requestBody: any = {
            jwt_token: token,
            region_id: regionId,
            lang: lang,
            startDate: TimeUtil.formatDateToISO(startDate),
            endDate: TimeUtil.formatDateToISO(endDate)
        };

        if (this.env.HISTORY_CHANNEL) {
            requestBody.channel = this.env.HISTORY_CHANNEL;
        }

        const headers = new HttpHeaders({
            'Accept': 'application/pdf',
            'Content-Type': 'application/json'
        });

        try {
            const blob = await this.http.put(url, requestBody, { headers, responseType: 'blob' }).toPromise();
            saveAs(blob, 'purchase-history.pdf');
        } catch (error) {
            console.error('Error downloading purchase history PDF:', error);
        }
    }
}
